import { Component, OnInit, AfterViewInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { Input, Output, EventEmitter }  from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { Place } from '@app/modules/model/place';
import { Ticket } from '@app/modules/model/ticket';
import { TicketProduct } from '@app/modules/model/ticketproduct';
import { Product } from '@app/modules/model/product';
import { ProductOption } from '@app/modules/model/productoption';

import { languageSupport, languageService } from '@app/modules/common/language';
import { deviceService, WSInput } from '@app/modules/sockets';
import { dataService } from '@app/modules/data';
import { viewService } from '@app/modules/view';

import { UiInput } from '@app/components/common/ui-input/ui-input';

@Component({
    selector: 'ui-product-view',
    templateUrl:'./ui-view.html',
    styleUrls: [ 
        './ui-view.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiProductView extends languageSupport implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input('place') _place: Place = null;
    @Input('ticket') _ticket: Ticket = null;
    @Input('ticketproduct') _ticketproduct: TicketProduct = null;
    @Input('product') _product: Product = null;
    @Input('amount') _itemstocart: number = 1;
    @Input('options') _itemOptions: Array <ProductOption> = [];
    @Input('canadd') _canadd: boolean = true;

    private _subscriptions = [];
    private _user_subscription = null;

    private get user(){
        // Usuario EFECTIVO (fuente única data.EffectiveUser): si no, un camarero con
        // PIN heredaría el permiso de invitar (CanCompliment) del owner, y bajo
        // bloqueo caería al owner de sesión.
        return this.data.EffectiveUser;
    }

    /***********************************/
    /* DATA COMPONENTS                 */
    /***********************************/

    IsVarious(_product){
        return _product.IsVarious;
    }

    IsGeneric(_product){
        return _product.IsGeneric;
    }

    IsProduct(_product){
        return !this.IsVarious(_product) && !this.IsGeneric(_product);
    }

    private IsSattisfied(depend){
        let _category = null;
        for (let category of this._editable.categories){
            if (category.IsCopyOf(depend.option.category)){
                _category = category;
            }
        }

        if (_category){
            if (_category.type == 'SINGLE'){
                return (!!_category['selected'] && (_category['selected'].IsCopyOf(depend.option)));
            }
            else {
                return _category.options.some(
                (option) => {
                    return (depend.option.IsCopyOf(option)) && (option['checked'] === true);
                });
            }
        }

        return false;
    }

    get CanCompliment(){
        return this._place.AllowCompliment(this.user);
    }

    HideCategory(category){
        if (category.depends.length == 0){
            return false;
        }
        
        let _sattisfied = category.depends.some(
        (depend) => {
            return this.IsSattisfied(depend);;
        });
    
        // disable al checked options if not sattisfied
        if (!_sattisfied){ 
            if (category.type == 'SINGLE'){
                category['selected'] = null;
            }
            else {
                for(let _option of category.options){
                    _option['checked'] = false;
                }    
            }
        }

        return !_sattisfied;
    }

    private _validSingle(category){
        if (!this.HideCategory(category)){
            return ('selected' in category) && (category['selected']) && (category['selected'].IsAvailable);
        }

        return true;
    }

    private _validFixed(category){
        let _count = 0;
        for(let _option of category.options){
            if ((_option['checked'] === true) && (_option.IsAvailable)){
                _count++;
            }
        }

        return (_count >= category.fxmin) && (_count <= category.fxmax);
    }

    private get isReady(){
        for(let _category of this._editable.categories){
            let _selected = (!_category.IsAvailable);
            switch(_category.type){
                case 'SINGLE':
                    _selected = _selected || this._validSingle(_category);
                    break;
                case 'MULTIPLE':
                    _selected = _selected || true;
                    break;
                case 'FIXED':
                    _selected = _selected || this._validFixed(_category);
                    break;
            }

            if (!_selected){
                return false;
            }
        }

        return true;
    }

    get Price(){
        if (this.ToTicket){
            let _price = Number(this.ToTicket.TotalPrice || 0);
            if (_price !== null){
                let _parts = (Math.abs(_price)).toString().split('.');
    
                let _int = _parts[0];
                let _dec = (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : "00";
                let _str = _price.toFixed(2);
                let _neg = (_price < 0); 
    
                return {
                    Int: _int,
                    Dec: _dec,
                    Str: _str,
                    Neg: _neg
                }    
            }

            return null;
        }

        return this._editable.Price;
    }

    private TotalPrice(selection) {
        if (this.RepriceForm && this.RepriceForm.get('reprice').value){
            return this.RepriceForm.get('price').value;
        }   
        else {
            let _price: number = this._editable && this._editable.price ? this._editable.price : 0;;
        
            // check to use the market price instead of the product price
            if (this._editable.MarketPrice){
                _price = parseFloat(this.ProductForm.get('market').value);
            }
    
            // calculate the price based on the weight and the current price per kg
            if (this._editable.IsWeighted){
                _price = _price * (parseFloat(this.ProductForm.get('weight').value)/1000);
            }
    
            for(let _option of selection.options){
                _price += _option.price;
            }
            
            return  Math.round(_price * 100) / 100;
        }
    }

    private _selection = null;
    get ToTicket(){
        if ((this._editable.MarketPrice || this._editable.IsWeighted) && this.ProductForm.invalid) {
            return null;    // missing price options
        }

        if (!this._selection){
            this._isDirty = true;

            let _selection = null;
            if (this.isReady){
                _selection = {
                    product: this._editable.CopyOf,
                    options: [],
                    TotalPrice: 0
                };

                for(let _category of this._editable.categories){
                    if (!_category.IsAvailable || this.HideCategory(_category)) {
                        continue;
                    }
            
                    if (_category.type == 'SINGLE'){
                        _selection.options.push(_category['selected'].CopyOf);
                    }
                    else {
                        for(let _option of _category.options){
                            if (_option['checked'] === true){
                                _selection.options.push(_option.CopyOf);
                            }
                        }
                    }
                }

                _selection.TotalPrice = this.TotalPrice(_selection);
            }

            this._selection = _selection;    
        }

        return this._selection;
    }

    /***********************************/
    /* STARTS HERE                     */
    /***********************************/

    public _notes: string = null;

    constructor(private lang: languageService, private change: ChangeDetectorRef, private devsvc: deviceService, private data: dataService, public view: viewService) {
        super(lang, change);
    }

    private _SelectOption(_option){
        for(let category of this._editable.categories){
            if (category.type == 'SINGLE'){
                for (let option of category.options){
                    if (option.CopyOf == _option) {
                        category['selected'] = option;
                    }
                }    
            }
            else {
                for (let option of category.options){
                    if (option.CopyOf == _option) {
                        option['checked'] = true;
                    }
                }
            }
        }
    }

    private _Initialize() {
        if (this._ticketproduct) {
            this._editable = this._ticketproduct.product.Copy();
            this._notes = this._ticketproduct.comments;
            for(let _option of this._ticketproduct.options){
                this._SelectOption(_option.option);
            }
        }
        else {
            this._editable = this._product.Copy();
            for(let _option of this._itemOptions){
                this._SelectOption(_option);
            }
        }

        if (this._ticket){
            this._subscriptions.push(
                this._ticket.OnRefresh.subscribe(
                data => {
                    this.change.markForCheck();
                })
            );      
        }
        
        if (this.user){
            this._user_subscription = this.user.OnRefresh.subscribe(
            data => {
                this.change.markForCheck();
            });        
        }
    
        // refresh the forms
        this.LoadProductForm();
        this.LoadVariousForm();
        this.LoadGenericForm();
        this.LoadRepriceForm();

        // initialize input devices
        this._InitializeScale();
    }

    @ViewChild('scrollable', {static: false}) _scrollable: ElementRef;    
    private _Finalize(){
        if (this._scrollable){
            this._scrollable.nativeElement.scrollTop = 0;
        }

        this._notes = null;         // clear the notes 
        this._selection = null;     // clear selection

        for(let _subscription of this._subscriptions){
            _subscription.unsubscribe();
        }
        this._subscriptions = [];

        if (this._user_subscription){
            this._user_subscription.unsubscribe();
            this._user_subscription = null;
        }

        // finalize input devices
        this._FinalizeScale();
    }

    public _editable: Product = null;
    ngOnInit(){
        this._Initialize();
    }

    @ViewChild('marketinput', {static: false}) _marketinput: UiInput;    
    @ViewChild('weightinput', {static: false}) _weightinput: UiInput;
    @ViewChild('variousprice', {static: false}) _variousprice: UiInput;
    @ViewChild('genericprice', {static: false}) _genericprice: UiInput;

    ngAfterViewInit(){
        this.change.markForCheck();

        setTimeout(() => {
            if (this._editable.MarketPrice && this._marketinput){
                this._marketinput.SetFocus();
                return;     // already provided focus
            }    

            if (this._editable.IsWeighted && this._weightinput){
                this._weightinput.SetFocus();                
                return;     // already provided focus
            }

            if (this.IsVarious(this._editable) && this._variousprice){
                this._variousprice.SetFocus();                
                return;     // already provided focus
            }

            if (this.IsGeneric(this._editable) && this._genericprice){
                this._genericprice.SetFocus();                
                return;     // already provided focus
            }
        }, 250);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((this._product == null) && (this._ticketproduct != null)){
            this._product = this._ticketproduct.product;
        }

        if (('_product' in changes) || ('_ticketproduct' in changes)){
            this._Finalize();
            this._Initialize();    
        }
    }

    ngOnDestroy(){
        super.OnDestroy();
        this._Finalize();
    }

    /***********************************/
    /* EVENT HANDLERS                  */
    /***********************************/
    
    private _isDirty = false;
    private _prnotes = null;

    @Output('onPanel') _onPanel = new EventEmitter<any>();
    @Output('onClose') _onClose = new EventEmitter<any>();

    OnClose(){
        this._isDirty = false;
        this._onPanel.emit({
            scnd: null,
            args: null
        })

        this._onClose.emit();
    }

    OnSelection(category){
        this._isDirty = true;

        let _disabled = false;
        if (category.type == 'FIXED'){
            let _count = 0;
            for(let _option of category.options){
                if ((_option.IsAvailable) && (_option['checked'])){
                    _count++;
                }
            }
            _disabled = (_count >= category.fxmax);
        }

        for(let _option of category.options){
            _option['disabled'] = _disabled && (!_option['checked']);
        }

        this._selection = null;
        this.change.markForCheck();
    }

    OnComments(){
        this._isDirty = this._isDirty || (this._notes != this._prnotes);
        this._prnotes = this._notes;
    }

    OnDescription(option){
        option['expand'] = !option['expand'];
    }

    get ToInsert(){
        if (this._editable.IsVarious){
            return true;            
        }

        return (this._editable.IsAvailable);
    }

    get ToUpdate(){
        if (this._editable.IsVarious){
            return (this._ticketproduct != null);
        }

        if (this.RepriceForm){
            this._isDirty ||= this.RepriceForm.dirty;
        }

        return (this._ticketproduct != null) && ((this._isDirty));
    }

    get ItemSize(){
        if (this._product.IsWeighted){
            return (this._itemstocart * (this.ProductForm.get('weight').value / 1000)).toFixed(2);
        }

        return this._itemstocart.toString();
    }

    get CanDelItem(){
        return this._canadd && (this._itemstocart > 1);
    }

    OnDelItem(event){
        event.stopPropagation();
        if (this.CanDelItem){
            this._itemstocart--;
            this.change.markForCheck();    
        }
    }

    get CanAddItem(){
        return this._canadd && (this._itemstocart < 99);
    }

    OnAddItem(event){
        event.stopPropagation();
        if (this.CanAddItem){
            this._itemstocart++;
            this.change.markForCheck();            
        }
    }

    OnInsert(){
        let _selection = this.ToTicket;
        if (_selection){
            let _productinfo = {
                fixedp: null,
                market: this.ProductForm.get('market').value ? parseFloat(this.ProductForm.get('market').value) : null,
                weight: this.ProductForm.get('weight').value ? parseFloat(this.ProductForm.get('weight').value) : null
            };

            for(let size=0; size < this._itemstocart; size++){
                let _ticketproduct = this._ticket.ToCart(_selection.product, _selection.options, _productinfo, this._notes);
                // El reprice/invitación (precio fijo, p.ej. cargo 0) SOLO se aplica
                // con permiso de invitar del usuario EFECTIVO: el checkbox se oculta
                // sin permiso, pero los controles de precio/invitar leían solo
                // RepriceForm.reprice (inicializado desde fixed) -> un camarero sin
                // permiso podía aplicar un cargo 0.
                _ticketproduct.fixed = (this.RepriceForm != null) && (this.RepriceForm.get('reprice').value) && this.CanCompliment;
                if (_ticketproduct.fixed){
                    _ticketproduct.charge = Number(this.RepriceForm.get('price').value) || 0;
                }
    
                if (_ticketproduct){
                    this._ticket.CartAdd(_ticketproduct);
                }    
            }
    
            this._itemstocart = 1;
            this.change.markForCheck();
        }

        this._ticket.DoRefresh('TICKETPRODUCT', true);
        this.OnClose();
    }

    OnUpdate(){
        let _selection = this.ToTicket;
        if (_selection){
            // reprice/invitación solo con permiso de invitar (ver OnAccept arriba).
            let _reprice = (this.RepriceForm) && (this.RepriceForm.get('reprice').value) && this.CanCompliment;
            let _info = {
                market: this.ProductForm.get('market').value ? Number(this.ProductForm.get('market').value) : null,
                weight: this.ProductForm.get('weight').value ? Number(this.ProductForm.get('weight').value) : null,
                fixed: _reprice,
                charge: _reprice ? (Number(this.RepriceForm.get('price').value) || 0) : null
            };

            // Mutar la MISMA linea in-place (CartModify), no DE+AC (CartSet).
            // Ej: añades café → modificas → pones avena antes de MANDAR: cocina
            // ve un solo NUEVO con el extra, no eliminado+nuevo. Si la comanda
            // ya salio a cocina, CartModify emite 'mod' (MODIFICADOS).
            this._ticket.CartModify(this._ticketproduct, _selection.options, _info, this._notes);
            this.change.markForCheck();
        }

        this._ticket.DoRefresh('TICKETPRODUCT', true);
        this.OnClose();
    }

    OnCancel(){
        this.OnClose();
    }

    OnPriceChange(_formcontrol){
        let _value = _formcontrol.value;
        if (_value){
            _formcontrol.setValue(parseFloat(_value.toString().replace(",", ".")).toFixed(2));            
        }
        else {
            _formcontrol.setValue(null);
        }
    }

    /***********************************/
    /* PRODUCT EDITION                 */
    /***********************************/

    private _productForm = null;
    get ProductForm(){
        if (!this._editable.IsProduct){
            return null;
        }

        if (this._productForm == null){
            this.LoadProductForm();
        }
        return this._productForm;
    }

    private LoadProductForm(){
        if (!this._editable.IsProduct){
            return;
        }

        let _market = (this._ticketproduct) ? this._ticketproduct.market : null;
        let _weight = (this._ticketproduct) ? this._ticketproduct.weight : null;

        if (this._productForm == null){
            this._productForm = new FormGroup({
                market: new FormControl(_market, [ 
                    // no validators 
                ]),
                weight: new FormControl(_weight, [
                    // no validators
                ])
            });
        }
        else {
            this._productForm.patchValue({
                market: _market,
                weight: _weight
            });
        }

        if (this._editable.MarketPrice){
            this._productForm.get('market').setValidators([ Validators.required ]);
            this._productForm.get('market').setErrors(null);
            this._productForm.get('market').updateValueAndValidity();
        }

        if (this._editable.IsWeighted){
            this._productForm.get('weight').setValidators([ Validators.required ]);
            this._productForm.get('weight').setErrors(null);
            this._productForm.get('weight').updateValueAndValidity();            
        }

        this._productForm.markAsPristine();
        setTimeout(() => {
            this._productForm.markAsPristine();
        }, 0);

        this.change.markForCheck();
    }

    /***********************************/
    /* VARIOUS EDITION                 */
    /***********************************/

    private _variousForm = null;
    get VariousForm(){
        if (!this._editable.IsVarious){
            return null;
        }

        if (this._variousForm == null){
            this.LoadVariousForm();
        }
        return this._variousForm;
    }

    private LoadVariousForm(){
        if (!this._editable.IsVarious){
            return;
        }

        let _price = this._editable.price;
        let _type = this._editable.type;
        let _description = this._editable.description;

        if (this._variousForm == null){
            this._variousForm = new FormGroup({
                price: new FormControl(_price, [ 
                    Validators.required 
                ]),
                description: new FormControl(_description, [
                    // no validators
                ]),
                type: new FormControl(_type, [
                    Validators.required 
                ])
            });
        }
        else {
            this._variousForm.patchValue({
                price: _price,
                description: _description,
                type: _type
            });
        }

        this._variousForm.markAsPristine();
        setTimeout(() => {
            this._variousForm.markAsPristine();
        }, 0);
    }

    get CanVariousInsert(){
        return this.VariousForm.dirty && this.VariousForm.valid;
    }

    OnVariousInsert(){
        // update the product with the form data
        this._product.price = Number(this.VariousForm.get('price').value);
        this._product.description = this.VariousForm.get('description').value;
        this._product.type = this.VariousForm.get('type').value;

        // add the various to the place and ticket
        this._place.AddProduct(this._product);

        for(let size=0; size < this._itemstocart; size++){
            let _ticketproduct = this._ticket.ToCart(this._product, [], null, this._product.description);
            if (_ticketproduct){
                this._ticket.CartAdd(_ticketproduct);
            }
        }

        this._ticket.DoRefresh('TICKETPRODUCT', true);

        this._product.DoCommit().then(data => {
            if (data){
                this.OnClose();
            }
        });
    }

    get CanVariousUpdate(){
        return this.VariousForm.dirty && this.VariousForm.valid;
    }

    OnVariousUpdate(){
        let _product = this._ticketproduct.product;        

        // update the product with the form data
        _product.price = Number(this.VariousForm.get('price').value);
        _product.description = this.VariousForm.get('description').value;
        _product.type = this.VariousForm.get('type').value;

        // update the various in the ticket
        let _ticketproduct = this._ticket.ToCart(_product, [], null, _product.description);
        if ((this.RepriceForm) && (this.RepriceForm.get('reprice').value)){
            _ticketproduct.charge = this.RepriceForm.get('price').value || 0;
        }

        this._ticket.CartSet(this._ticketproduct, _ticketproduct);
        this._ticket.DoRefresh('TICKETPRODUCT', true);

        _product.DoCommit().then(data => {
            if (data){
                this.OnClose();
            }
        });
    }

    /***********************************/
    /* GENERIC EDITION                 */
    /***********************************/

    private _genericForm = null;
    get GenericForm(){
        if (!this._editable.IsGeneric){
            return null;
        }

        if (this._genericForm == null){
            this.LoadGenericForm();
        }
        return this._genericForm;
    }

    private LoadGenericForm(){
        if (!this._editable.IsGeneric){
            return;
        }

        let _price = this._editable.price;
        let _type = (this._editable.type == 'UNK') ? null : this._editable.type;
        let _description = this._editable.description;

        if (this._genericForm == null){
            this._genericForm = new FormGroup({
                price: new FormControl(_price, [ 
                    Validators.required 
                ]),
                description: new FormControl(_description, [
                    // no validators
                ]),
                type: new FormControl(_type, [
                    Validators.required 
                ])
            });
        }
        else {
            this._genericForm.patchValue({
                price: _price,
                description: _description,
                type: _type
            });
        }

        this._genericForm.markAsPristine();
        setTimeout(() => {
            this._genericForm.markAsPristine();
        }, 0);
    }

    get CanGenericInsert(){
        return this.GenericForm.dirty && this.GenericForm.valid;
    }

    OnGenericInsert(){
        let _product = new Product(null, this.data)
        if (_product){
            // copy product values into the new product
            _product.place = this._product.place;
            _product.name = this._product.name;
            _product.photo = this._product.photo;
            _product.parent = this._product.parent;
            _product.ctgref = this._product.ctgref;

            // is not a group and is not a generic product
            _product.isgroup = false;
            _product.generic = false;

            // complete the product with the form data
            _product.price =  Number(this.GenericForm.get('price').value);
            _product.description = this.GenericForm.get('description').value;
            _product.type = this.GenericForm.get('type').value;

            // act as a various product
            _product.status = 'OT';
        }

        // add the various to the place and ticket
        this._place.AddProduct(_product);

        for(let size=0; size < this._itemstocart; size++){
            let _ticketproduct = this._ticket.ToCart(_product, [], null, _product.description);
            if (_ticketproduct){
                this._ticket.CartAdd(_ticketproduct);
            }
        }

        this._ticket.DoRefresh('TICKETPRODUCT', true);

        this.OnClose();
    }

    get CanGenericUpdate(){
        return this.GenericForm.dirty && this.GenericForm.valid;
    }

    OnGenericUpdate(){
        let _product = this._ticketproduct.product;

        // update the product with the form data
        _product.price = Number(this.VariousForm.get('price').value);
        _product.description = this.VariousForm.get('description').value;
        _product.type = this.VariousForm.get('type').value;

        // update the various in the ticket
        let _ticketproduct = this._ticket.ToCart(_product, [], null, _product.description);
        if ((this.RepriceForm) && (this.RepriceForm.get('reprice').value)){
            _ticketproduct.charge = this.RepriceForm.get('price').value || 0;
        }

        this._ticket.CartSet(this._ticketproduct, _ticketproduct);
        this._ticket.DoRefresh('TICKETPRODUCT', true);

        _product.DoCommit().then(data => {
            if (data){
                this.OnClose();
            }
        });
    }

    /***********************************/
    /* PRODUCT REPRICE                 */
    /***********************************/

    private _repriceform = null;
    get RepriceForm(){
        if (!this._ticketproduct){
            return null;
        }

        if (this._repriceform == null) {
            this.LoadRepriceForm();
        }
        return this._repriceform;
    }

    private LoadRepriceForm(){
        if (!this._ticketproduct){
            return;
        }     

        let _reprice = this._ticketproduct.fixed;
        let _price = this._ticketproduct.charge;
        let _invite = (this._ticketproduct.charge == 0);

        if (this._repriceform == null) {
            this._repriceform = new FormGroup({
                reprice: new FormControl(_reprice, [
                    // no validators
                ]),
                price: new FormControl(_price, [
                    Validators.required
                ]),
                invite: new FormControl(_invite, [
                    // no validators
                ])
            });   
        }
        else {
            this._repriceform.patchValue({
                reprice: _reprice,
                price: _price,
                invite: _invite
            });

            this._repriceform.markAsPristine();
        }

        this.OnTicketInvite();
    }

    OnTicketPrice(){
        this._selection = null;
        this.change.markForCheck();
    }

    OnTicketInvite(){
        if (this.ToTicket){
            let _invite = this.RepriceForm.get('invite').value;
            if (_invite){
                this.RepriceForm.get('price').setValue(0);
                this.RepriceForm.get('price').disable();
            }
            else {
                let _charge = this._ticketproduct.charge;
                if (!_charge){
                    let _totalPrice = this._editable.price;
                    for(let _option of this.ToTicket.options){
                        _totalPrice += _option.price;
                    }    
                    _totalPrice = Math.round(_totalPrice * 100) / 100;        
                }

                this.RepriceForm.get('price').setValue(_charge);
                this.RepriceForm.get('price').enable();
            }    
        }

        this._selection = null;
        this.change.markForCheck();
    }

    /***************************/
    /* CONNECT TO SCALE        */
    /***************************/

    private _scale: WSInput = null;
    private _scale_read_subscription = null;
    private _scale_lost_subscription = null;

    // private _scale_connection = null;
    private _scaleconnected = false;
    private _scaleconnecting = false;
    private _scale_lastvalue = null;

    get PlaceScale(){
        let _localservice = this._place.till;
        if (!_localservice){
            return null;    // no local service provided
        }

        if (!_localservice.connect?.connect){
            return null;    // this is not a direct connection
        }

        for (let _scale of this._place.till.scales){
            if (_scale.IsValid){
                return _scale;
            }
        }

        return null;
    }

    get ScaleConnected(){
        return this._scaleconnected;
    }
        
    set ScaleConnected(value){
        if (this._scaleconnected != value){
            (value) ? this._InitializeScale() : this._FinalizeScale();
        }
    }

    OnScaleConnect(){
        this.ScaleConnected = !this.ScaleConnected;
        this.change.markForCheck();
    }

    private async _InitializeScale(){
        if (!this.PlaceScale){
            return;     // nothing to initalize
        }

        if (!this._editable.IsWeighted){
            return;     // nothing to be weighted
        }

        if (this._scaleconnecting || this._scaleconnected){
            return;     // already intialized
        }

        let _host = null;
        let _port = null;
        let _name = null;

        if (this._place && this._place.till){
            this._scaleconnecting = true;
            setTimeout(() => {
                this._scaleconnecting = false;
            }, 1000);

            _name = this._place.till.name;
            _host = this._place.till.wshost;
            _port = this._place.till.wsport;            

            this._scale = await this.devsvc.WSInputScale(_host, _port, _name, this.PlaceScale);
            if (this._scale){
                this._scaleconnected = !!(await this._scale.doIniRead());
                if(this._scaleconnected){
                    if (this._scale_read_subscription){
                        this._scale_read_subscription.unsubscribe();
                        this._scale_read_subscription = null;
                    }

                    if (this._scale_lost_subscription){
                        this._scale_lost_subscription.unsubscribe();
                        this._scale_lost_subscription = null;
                    }

                    this._scale_read_subscription = this._scale.OnInputRead.subscribe(
                    data => {
                        if (this._scale_lastvalue != data){
                            this._scale_lastvalue = data;
                            this.ProductForm.get('weight').setValue(data);
                            this.OnTicketPrice();    
                        }
                    });

                    this._scale_lost_subscription = this._scale.OnInputLost.subscribe(
                    data => {
                        this._FinalizeScale();
                    });
                }
            }
            else {
                this._scaleconnected = false;
            }
        }

        this.change.markForCheck();
    }

    private async _FinalizeScale(){
        this._scaleconnected = false;

        if (this._scale_read_subscription){
            this._scale_read_subscription.unsubscribe();
            this._scale_read_subscription = null;
        }

        if (this._scale_lost_subscription){
            this._scale_lost_subscription.unsubscribe();
            this._scale_lost_subscription = null;
        }

        if (this._scale){
            await this._scale.OnDestroy();
            this._scale = null;
        }

        this.change.markForCheck();
    }
}
